// views/Settings/settingsUi.js
// ─────────────────────────────────────────────────────────────────────────────
// The small shared pieces every editor in the Settings menu uses: the form
// controls on the dashboard theme, a "last changed" line, and the read-only
// notice. One place, so the editors are forms and nothing else.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { THEME as C } from "../../components/dashboardTheme";
import { AppButton } from "../../components/ui";
import { apiPlatformSettingsHistory } from "../../api";

export const field = {
  label: {
    display: "block", fontSize: "var(--lk-settings-label-size, 11px)", fontWeight: 600, color: C.textLight,
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5,
  },
  input: {
    width: "100%", padding: "9px 12px", fontSize: "var(--lk-settings-input-size, 13px)", color: C.text,
    border: `1px solid ${C.border}`, borderRadius: C.radiusSm, background: "#fff", outline: "none",
  },
  help: { fontSize: 12, color: C.textLight, marginTop: 5, lineHeight: 1.5 },
  grid: (cols = 2) => ({ display: "grid", gridTemplateColumns: `repeat(var(--lk-settings-columns, ${cols}), minmax(0, 1fr))`, gap: 14 }),
  actions: { display: "flex", alignItems: "center", gap: 10, marginTop: 18, flexWrap: "wrap" },
};

export const fmtWhen = (d) =>
  d ? new Date(d).toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" }) : null;

/** "Last changed by X on date", read for the PlatformSetting keys an editor owns. */
export const LastChanged = ({ keys = [], refreshToken = 0 }) => {
  const [history, setHistory] = useState({});
  // The key list is compared as a string so a caller passing a fresh array
  // each render does not refetch on every render.
  const keyList = keys.join(",");
  useEffect(() => {
    if (!keyList) return;
    apiPlatformSettingsHistory(keyList.split(","))
      .then((res) => setHistory(res.data?.history || {}))
      .catch(() => setHistory({}));
  }, [keyList, refreshToken]);
  const rows = keys.map((k) => history[k]).filter((h) => h && h.updatedAt);
  if (!rows.length) {
    return <div style={{ fontSize: 12, color: C.textLight }}>Never changed on the dashboard; the shipped default applies.</div>;
  }
  const latest = rows.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
  const who = latest.updatedBy?.name || latest.updatedBy?.email || "a staff member";
  return (
    <div style={{ fontSize: 12, color: C.textLight }}>
      Last changed by <span style={{ color: C.textMid, fontWeight: 600 }}>{who}</span> on {fmtWhen(latest.updatedAt)}.
    </div>
  );
};

/** Shown above a form the signed-in person may read but not change. */
export const ReadOnlyNotice = ({ level = "manage" }) => (
  <div style={{
    background: C.amberSoft, border: `1px solid ${C.amber}55`, borderRadius: C.radiusSm,
    padding: "10px 14px", fontSize: 12, color: C.textMid, marginBottom: 16,
  }}>
    You can view this setting. Changing it needs {level === "manage" ? "a super administrator" : "the settings update permission"}; ask one in Access Control.
  </div>
);

export const SaveButton = ({ saving, disabled, children = "Save changes", ...rest }) => (
  <AppButton type="submit" disabled={saving || disabled} {...rest}>
    {saving ? "Saving..." : children}
  </AppButton>
);
